import { Link } from 'react-router-dom';
import {
  Download,
  ArrowRight,
  Sparkles,
  Zap,
  Code2,
  Shield,
  Globe,
  Cpu,
  Terminal,
  GitBranch,
  Puzzle,
  Palette,
  FlaskConical
} from 'lucide-react';
import { FlagIcon } from '@/components/ui/FlagIcon';
import { LiveCodeEditor } from '@/components/ui/LiveCodeEditor';
import { IDEBackground } from '@/components/ui/IDEBackground';
import { HeroBackground } from '@/components/ui/HeroBackground';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import { useTranslation } from 'react-i18next';

// Company logos for social proof
const trustedCompanies = [
  'Stripe', 'OpenAI', 'NVIDIA', 'Figma', 'Linear', 'Datadog', 'Adobe', 'Ramp'
];

// Feature icons for grid
const featureIcons = [Sparkles, Zap, Code2, Terminal, GitBranch, Puzzle];

export function HomePage() {
  const { t } = useTranslation();

  // Build features from translations
  const featureKeys = ['aiAgent', 'autocomplete', 'codebase', 'terminal', 'git', 'extensions'];
  const features = featureKeys.map((key, index) => ({
    icon: featureIcons[index],
    title: t(`home.features.${key}.title`),
    description: t(`home.features.${key}.description`)
  }));

  // Build testimonials from translations with dynamic initials
  const testimonials = [0, 1, 2, 3].map((index) => {
    const author = t(`home.testimonials.${index}.author`);
    const nameParts = author.split(' ');
    const avatar = nameParts.length >= 2
      ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
      : author.substring(0, 2).toUpperCase();
    return {
      quote: t(`home.testimonials.${index}.quote`),
      author,
      role: t(`home.testimonials.${index}.role`),
      company: t(`home.testimonials.${index}.company`),
      avatar
    };
  });

  // AI features list
  const aiFeatures = t('home.aiSection.features', { returnObjects: true }) as string[];

  // Support features list
  const supportItems = t('home.support.items', { returnObjects: true }) as string[];

  return (
    <>
      <SEO {...SEO_CONFIG.home} />
      <div>
      {/* Hero Section */}
      <section className="section-lg relative overflow-hidden min-h-[90vh] flex items-center">
        <HeroBackground />

        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* BETA + Made in Turkey Badge */}
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r from-accent/10 via-red-500/10 to-white/10 border border-accent/30 mb-6 animate-fade-in">
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-accent/20 border border-accent/30">
                <FlaskConical size={12} className="text-accent" />
                <span className="text-xs font-bold text-accent">BETA</span>
              </div>
              <div className="w-px h-4 bg-border-default" />
              <FlagIcon code="tr" size={20} />
              <span className="text-sm font-medium text-text-secondary">
                {t('hero.badge')}
              </span>
            </div>

            <h1 className="heading-xl mb-6">
              {t('home.hero.title')}{' '}
              <span className="gradient-text">{t('home.hero.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
              {t('hero.description')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/download" className="btn btn-primary btn-lg animate-pulse-glow">
                <Download size={20} />
                {t('hero.cta.download')}
              </Link>
              <Link to="/features" className="btn btn-secondary btn-lg">
                {t('hero.cta.learnMore')}
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>

          {/* IDE Preview - Live Code Editor */}
          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-[#1e1e2e] via-transparent to-transparent z-10 pointer-events-none" />
            <LiveCodeEditor />
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="section-sm border-y border-border-default">
        <div className="container">
          <p className="text-center text-text-tertiary text-sm mb-8">
            {t('home.trustedBy')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {trustedCompanies.map((company) => (
              <span
                key={company}
                className="text-text-tertiary text-lg font-semibold opacity-60 hover:opacity-100 transition-opacity"
              >
                {company}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section relative overflow-hidden">
        <IDEBackground />
        <div className="container relative z-10">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('home.featuresSection.title')}{' '}
              <span className="gradient-text">{t('home.featuresSection.titleHighlight')}</span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('home.featuresSection.subtitle')}
            </p>
          </div>

          <div className="grid-features">
            {features.map((feature) => (
              <div key={feature.title} className="card group">
                <div className="w-12 h-12 rounded-lg bg-accent-muted flex items-center justify-center mb-4 group-hover:bg-accent transition-colors">
                  <feature.icon className="text-accent group-hover:text-white transition-colors" size={24} />
                </div>
                <h3 className="heading-sm mb-2">{feature.title}</h3>
                <p className="text-text-secondary">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/features" className="btn btn-secondary">
              {t('home.featuresSection.exploreAll')}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* AI Agent Feature */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-muted text-accent text-sm mb-4">
                <Sparkles size={14} />
                {t('home.aiSection.badge')}
              </div>
              <h2 className="heading-lg mb-4">
                {t('home.aiSection.title')}
              </h2>
              <p className="text-text-secondary mb-6">
                {t('home.aiSection.description')}
              </p>
              <ul className="space-y-3 mb-8">
                {aiFeatures.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-text-secondary">
                    <div className="w-5 h-5 rounded-full bg-success/20 flex items-center justify-center">
                      <ArrowRight size={12} className="text-success" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/features" className="btn btn-primary">
                {t('home.aiSection.learnMore')}
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="relative">
              <div className="rounded-xl border border-border-default overflow-hidden bg-bg-card">
                <div className="p-4 border-b border-border-default flex items-center gap-2">
                  <Sparkles className="text-accent" size={18} />
                  <span className="text-sm font-medium">{t('home.aiSection.agentTitle')}</span>
                </div>
                <div className="p-4 space-y-4">
                  <div className="p-3 rounded-lg bg-bg-elevated">
                    <p className="text-sm text-text-secondary">
                      {t('home.aiSection.demo.userPrompt')}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-accent-glow border border-accent/20">
                    <p className="text-sm text-text-primary mb-2">
                      {t('home.aiSection.demo.aiResponse')}
                    </p>
                    <ul className="text-sm text-text-secondary space-y-1">
                      {(t('home.aiSection.demo.steps', { returnObjects: true }) as string[]).map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('home.testimonialsSection.title')}
            </h2>
            <p className="text-text-secondary">
              {t('home.testimonialsSection.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial) => (
              <div key={testimonial.author} className="testimonial-card">
                <blockquote className="mb-4">
                  "{testimonial.quote}"
                </blockquote>
                <div className="testimonial-author">
                  <div className="w-10 h-10 rounded-full bg-accent-muted flex items-center justify-center text-accent font-semibold text-sm">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-medium text-text-primary">{testimonial.author}</div>
                    <div className="text-sm text-text-tertiary">
                      {testimonial.role} {t('home.testimonialsSection.at')} {testimonial.company}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Enterprise */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="card">
              <Shield className="text-accent mb-4" size={32} />
              <h3 className="heading-sm mb-2">{t('home.support.security.title')}</h3>
              <p className="text-text-secondary">
                {t('home.support.security.description')}
              </p>
            </div>
            <div className="card">
              <Globe className="text-accent mb-4" size={32} />
              <h3 className="heading-sm mb-2">{t('home.support.crossPlatform.title')}</h3>
              <p className="text-text-secondary">
                {t('home.support.crossPlatform.description')}
              </p>
            </div>
            <div className="card">
              <Cpu className="text-accent mb-4" size={32} />
              <h3 className="heading-sm mb-2">{t('home.support.aiModels.title')}</h3>
              <p className="text-text-secondary">
                {t('home.support.aiModels.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-lg" id="download">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-lg mb-4">
              {t('home.cta.title')}
            </h2>
            <p className="text-text-secondary mb-8">
              {t('home.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/download" className="btn btn-primary btn-lg">
                <Download size={20} />
                {t('hero.cta.download')}
              </Link>
              <Link to="/pricing" className="btn btn-secondary btn-lg">
                {t('nav.pricing')}
              </Link>
            </div>
            <p className="text-text-tertiary text-sm mt-4">
              macOS, Windows ve Linux için mevcut
            </p>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
